import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

// Coalesce identical concurrent GETs: plusieurs cartes du dashboard demandent le même
// endpoint au même instant (streak, profile, chapters...) — une seule requête réseau part.
const inflightGets = new Map();

const withGetDedupe = (instance) => {
  const originalGet = instance.get.bind(instance);
  instance.get = (url, config) => {
    const key = `${url}|${JSON.stringify(config?.params || null)}`;
    const pending = inflightGets.get(key);
    if (pending) return pending;
    const request = originalGet(url, config).finally(() => inflightGets.delete(key));
    inflightGets.set(key, request);
    return request;
  };
  return instance;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('odys_token'));
  const [loading, setLoading] = useState(true);

  const api = withGetDedupe(axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    timeout: 30000, // default 30s
  }));

  // Endpoints allowed to fail with 401/403 without triggering a global logout.
  // Prevents AuthCallback from being redirected mid-flow when Google refuses.
  const skipLogoutEndpoints = ['/auth/google/'];

  api.interceptors.request.use((config) => {
    const currentToken = localStorage.getItem('odys_token');
    if (currentToken) {
      config.headers.Authorization = `Bearer ${currentToken}`;
    }
    // Extend timeouts for LLM-backed endpoints (Article 50 AI Act)
    const longTimeoutEndpoints = [
      { pattern: '/translate-fr', ms: 180000 },
      { pattern: '/generate-summary', ms: 180000 },
      { pattern: '/sync-history', ms: 600000 },
      { pattern: '/batch-translate', ms: 600000 },
    ];
    const match = longTimeoutEndpoints.find(({ pattern }) => (config.url || '').includes(pattern));
    if (match) config.timeout = match.ms;
    return config;
  });

  api.interceptors.response.use(
    (response) => response,
    (error) => {
      const url = error.config?.url || '';
      const shouldSkip = skipLogoutEndpoints.some((ep) => url.includes(ep));
      if (error.response?.status === 401 && !shouldSkip) {
        logout();
      }
      return Promise.reject(error);
    }
  );

  useEffect(() => {
    const initAuth = async () => {
      // CRITICAL: if returning from OAuth callback, skip the /me check —
      // AuthCallback will exchange the session_id and set our token first.
      if (typeof window !== 'undefined' && window.location.hash && window.location.hash.includes('session_id=')) {
        setLoading(false);
        return;
      }
      if (token) {
        try {
          const response = await api.get('/auth/me');
          setUser(response.data);
        } catch (error) {
          console.error('Auth init error:', error);
          localStorage.removeItem('odys_token');
          setToken(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    // Normalize email to lowercase for case-insensitive login
    const normalizedEmail = email.trim().toLowerCase();
    const response = await api.post('/auth/login', { email: normalizedEmail, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('odys_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('odys_token');
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (data) => {
    await api.put('/auth/profile', data);
    const response = await api.get('/auth/me');
    setUser(response.data);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const refreshUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      console.error('Refresh user error:', error);
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    updateProfile,
    updateUser,
    refreshUser,
    api,
    isAdmin: user?.role === 'admin',
    isMajor: user?.role === 'major',
    isAmbassadeur: user?.role === 'ambassadeur',
    canCreateEvents: ['admin', 'major', 'ambassadeur'].includes(user?.role),
    canModerate: ['admin', 'major'].includes(user?.role),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
